import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { sectorDetailData } from '../../../data/sectorData';

const getPotentialStyle = (potential: string): string => {
  return potential === 'VERY HIGH' ? 'text-data-green' : 'text-data-amber';
};

interface AIPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const AIPanel: React.FC<AIPanelProps> = ({ isMinimized, onMinimize }) => {
  const { ai } = sectorDetailData;
  const { market, subsectors, metrics, gaps, regulations } = ai;

  return (
    <Panel title="AI / MACHINE LEARNING" subtitle={`STRONG BUY • Score ${ai.score}`} status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full gap-2 overflow-auto custom-scrollbar">
        {/* Header Stats */}
        <div className="grid grid-cols-3 gap-2 p-2 bg-terminal-bg rounded">
          <div className="text-center">
            <div className="text-xs text-gray-500">TAM 2024 → 2028</div>
            <div className="text-sm font-bold">
              <span className="text-white">${market.tam2024}B</span>
              <span className="text-gray-500 mx-1">→</span>
              <span className="text-data-green">${market.tam2028}B</span>
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">CAGR</div>
            <div className="text-sm font-bold text-data-green">{market.cagr}%</div>
            <div className="text-xs text-data-green bg-data-green/20 px-1.5 rounded mt-0.5">HIGHEST</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">PENETRATION</div>
            <div className="text-sm font-bold text-white">{market.penetration}%</div>
            <div className="w-full h-1 bg-terminal-border rounded mt-1">
              <div className="h-full bg-data-green rounded" style={{ width: `${(market.penetration / market.targetPenetration) * 100}%` }} />
            </div>
          </div>
        </div>

        {/* Subsectors */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="text-xs text-gray-500 uppercase mb-1.5">Subsectors (by CAGR)</div>
          <div className="space-y-1.5">
            {subsectors.map((sub) => (
              <div key={sub.name} className="flex items-center text-xs gap-2">
                <span className="text-gray-300 w-24 truncate">{sub.name}</span>
                <span className="text-white w-10 font-mono">${(sub.size * 1000).toFixed(0)}M</span>
                <span className="text-data-green w-12 font-mono">+{sub.cagr}%</span>
                <span className="text-gray-500 w-12 truncate">{sub.leader}</span>
                <span className={`flex-1 text-right ${sub.gap === 'WIDE OPEN' ? 'text-data-green font-medium' : 'text-gray-400'}`}>
                  {sub.gap === 'WIDE OPEN' ? '★ ' : ''}{sub.gap}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Ecosystem & Regulations */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-terminal-bg rounded p-2">
            <div className="text-xs text-gray-500 uppercase mb-1.5">Ecosystem</div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-400">Developers</span>
                <span className="text-white font-medium">{metrics.developers.toLocaleString()}+</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">AI Startups</span>
                <span className="text-white">{metrics.aiStartups}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Data Centers</span>
                <span className="text-white">{metrics.datacenters}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">IT Park</span>
                <span className="text-data-green">ACTIVE</span>
              </div>
            </div>
          </div>
          <div className="bg-terminal-bg rounded p-2">
            <div className="text-xs text-gray-500 uppercase mb-1.5">Incentives</div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-400">Tax</span>
                <span className="text-data-green font-bold">{regulations.taxBenefits}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Data Protection</span>
                <span className="text-data-amber">{regulations.dataProtection}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">AI Policy</span>
                <span className="text-data-amber">{regulations.aiPolicy}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Foreign Own</span>
                <span className="text-white">{regulations.foreignOwnership}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Opportunities */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="text-xs text-gray-500 uppercase mb-1.5">Top Opportunities</div>
          {gaps.map((g) => (
            <div key={g.opportunity} className="flex items-center text-xs py-1">
              <span className="text-white flex-1">{g.opportunity}</span>
              <span className={`w-16 ${getPotentialStyle(g.potential)}`}>[{g.potential}]</span>
              <span className="text-gray-400 w-16">{g.investment}</span>
              <span className="text-gray-500 w-14 text-right">{g.timeToMarket}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default AIPanel;
